// The contracts module is rendered as a tab on several different pages
// (contractors, partners, external technicians, warranties, subscriptions).
// Each page mounts the very same components and only passes its own `typeId`.
// That id travels with every request as `contract_type`, so the backend knows
// which flavor of contract the module is working on.
export const CONTRACT_TYPES = {
    PARTNER: 'PARTNER',
    EXTERNAL: 'EXTERNAL',
    COMPANY: 'COMPANY',
    WARRANTY: 'WARRANTY',
    SUBSCRIPTION: 'SUBSCRIPTION',
}

// Every endpoint the module talks to, in one place - the routes are shared by
// all contract types and only differ by the `contract_type` parameter.
export const ENDPOINTS = {
    list: 'contracts',
    add: 'contracts/add',
    edit: 'contracts/edit',
    find: 'contracts/find',
    delete: 'contracts/delete',
    terminate: 'contracts/terminate',
    stream: 'contracts/stream',
    send: 'contracts/send',
    // Подписът на управителя е един за партньора, не за отделен тип договор -
    // бекендът го поставя върху всеки генериран документ.
    defaultSignature: 'contracts/default-signature',
    defaultSignatureAdd: 'contracts/default-signature/add',
    defaultSignatureUpdate: 'contracts/default-signature/update',
}

// Клиентът подписва през публичен линк, затова тези заявки се правят с токена
// от адреса и без изобщо да минават през авторизация.
//
// Токенът се проверява през самостоятелен маршрут, който стои извън групата с
// авторизация и приема токена в тялото на заявката: { token: ... }.
export const VERIFY_TOKEN = 'contracts/verify-token'

export const publicSignEndpoint = token => 'contracts/sign/' + encodeURIComponent(token)

// Адресът на готовия PDF се връща от подписването - приемаме обичайните имена.
export const pdfUrl = data => {
    if (!data) {
        return ''
    }

    if (typeof data === 'string') {
        return data
    }

    return data.url || data.pdf_url || data.document_url || data.file_url || data.link || ''
}

// Отговорът на default-signature носи адреса на изображението под различни
// имена в зависимост от това дали идва от четене, или от качване.
export const signatureUrl = data => {
    if (!data) {
        return ''
    }

    if (typeof data === 'string') {
        return data
    }

    return data.file_url || data.file || data.image_url || data.image || data.url || data.path || ''
}

// A contract is always signed with a counterparty, but which list that
// counterparty comes from depends on the type. Only COMPANY is in use for now;
// the others reuse the contractors list until their own dropdowns exist.
// Насрещните страни идват от един и същ маршрут за всички типове договори -
// типът пътува като параметър `type`, за да върне бекендът правилния списък.
export const CONTRACTORS_LIST = 'contracts/contractors/list'

// Ключ на react-query заявката за списъка. Държим го тук, за да може всяко
// опресняване (включително през сокета) да стъпва на един и същ префикс.
export const CONTRACTS_QUERY_KEY = 'contracts/list'

// Събитието, което бекендът излъчва, щом клиентът подпише договора.
export const CONTRACT_SIGNED_EVENT = 'ContractSigned'

// A contract that has been sent is frozen - it can no longer be edited or
// deleted, only viewed, resent or terminated. The row carries a `status`
// string as well, but the timestamps are what the lifecycle actually hangs on.
export const isSent = contract => Boolean(
    contract?.sent_at || contract?.status === 'sent'
)

// Последната стъпка - клиентът е подписал документа по публичния линк.
export const isSigned = contract => Boolean(
    contract?.signed_by_party_at || contract?.status === 'signed'
)

export const isTerminated = contract => Boolean(
    contract?.terminated_at || contract?.status === 'terminated'
)

export const isEditable = contract => !isSent(contract) && !isSigned(contract) && !isTerminated(contract)
